import { ATTR_NAME } from 'constants/attr-name'
import { CLASS_NAME } from 'constants/class-names'
import { CSSUtils } from 'utils/css'
import { EventType } from 'constants/enums'
import { StringUtils } from 'utils/string'
import { ApiService } from 'services/api/api.service'
import { IVideoPlaylist } from './video-playlist'
import { PlaylistStore } from './store'
import { TimeUtils } from 'utils/time'
import { Nullable } from 'constants/types'
import { AbstractObserver } from 'utils/abstract-classes/abstract-observer'
import { logger } from 'utils/logger'

const { videoPlaylist: videoPlaylistClass } = CLASS_NAME

export class Playlist extends AbstractObserver {
    public currentIndex = 0
    public _queueList: HTMLElement
    public _playlistId: string
    public _uniqueId: string
    private _playlistData: IVideoPlaylist

    /**
     * Get the playlistData
     */
    public get playlistData(): IVideoPlaylist {
        return this._playlistData
    }

    /**
     * Get the playlistId
     */
    public get playlistId(): string {
        return this._playlistId
    }

    /**
     * Get the uniqueId
     */
    public get uniqueId(): string {
        return this._uniqueId
    }

    /**
     * Get the playlist list section element
     */
    public get queueList(): HTMLElement {
        return this._queueList
    }

    /**
     * Get the playlist list button elements
     * get will only work after initial render
     */
    private get queueButtons(): HTMLElement[] {
        return this.getElements(`.${videoPlaylistClass.itemBtn}`, this.el)
    }

    /**
     * Get the playlist list button elements
     * get will only work after initial render
     */
    private get videoCount(): Nullable<HTMLElement> {
        return this.getElement(`.${videoPlaylistClass.numVideos}`, this.el)
    }

    /**
     * Initializes the video centre list.
     *
     * @param {HTMLElement} el
     * @param {HTMLElement} _queueList
     * @param {string} _playlistId
     * @param {string} _uniqueId
     */
    public constructor(protected el: HTMLElement) {
        super(el)
        const playlistId = el.dataset.playlistId
        const uniqueId = el.dataset.uniqueId
        const queueList = this.getElement(`.${videoPlaylistClass.queue}`, el)

        if (!playlistId || !uniqueId || !queueList) {
            return
        }

        this._playlistId = playlistId
        this._uniqueId = uniqueId
        this._queueList = queueList

        PlaylistStore.getInstance().add(this)
        this.activateObserver({ rootMargin: '200px' })
    }

    /**
     * Load the list content, only once
     */
    public render(): void {
        // if the element has data-playlist-json, the server-side JSON data
        if (this.el.dataset.playlistJson) {
            CSSUtils.removeClass(this.el, CLASS_NAME.list.listLoading)
            this.activate(JSON.parse(this.el.dataset.playlistJson))
            return
        }

        new ApiService()
            .fetchVideoPlaylist(this.playlistId)
            .then((data: IVideoPlaylist) => {
                CSSUtils.removeClass(this.el, CLASS_NAME.list.listLoading)
                this.activate(data)
            })
            .catch((err) => {
                CSSUtils.removeClass(this.el, CLASS_NAME.list.listLoading)
                this.deactivate()
                logger('error', err)
            })
    }

    /**
     * Initializer
     * queueList is already verified in constructor
     *
     * @param {IVideoPlaylist}
     */
    private activate(data: IVideoPlaylist): void {
        // Item is empty, deactivate
        if (!data || !data.items || !data.items.length) {
            this.deactivate()
            return
        }

        this._playlistData = data
        PlaylistStore.getInstance().initLeadVideo()

        // update the queue list with the remaining videos, up to limit
        this.queueList.innerHTML = this.renderPlaylistQueue()

        // attach events to newly created buttons
        if (this.queueButtons) {
            this.queueList.addEventListener(EventType.CLICK, (ev: MouseEvent) =>
                this.onClick(ev)
            )
        }

        // n Video(s)
        if (this.videoCount) {
            this.videoCount.innerHTML =
                1 === this._playlistData.pageInfo.totalResults
                    ? '1 Video'
                    : `${this._playlistData.pageInfo.totalResults} Videos`
        }

        return
    }

    /**
     * Loading failed
     */
    private deactivate(): void {
        this.el.remove()
        PlaylistStore.getInstance().remove(this.playlistId)
    }

    /**
     * Build the list items for the playlist
     *
     * @return {string} HTML
     */
    private renderPlaylistQueue(): string {
        const items = this._playlistData.items
        const listItems: string[] = []

        // generate item list HMTL for remaining items
        items.forEach((_, index) => {
            // account for slicing in passed index
            const playlistItem = this.renderPlaylistItem(index)
            listItems.push(playlistItem)
        })

        if (PlaylistStore.getInstance().isCurrentList(this)) {
            // Move the first item to the end
            listItems.push(listItems.splice(0, 1)[0])
        }

        return listItems.join('')
    }

    /**
     * Build the list items for the playlist
     *
     * @param {number} index Item index
     * @return {string} HTML
     */
    private renderPlaylistItem(index: number): string {
        const item = this._playlistData.items[index]
        const defaultSrcset: string = item.thumbnails.default
            ? `${item.thumbnails.default.url} ${item.thumbnails.default.width}w,`
            : ''
        const mediumSrcset: string = item.thumbnails.medium
            ? `${item.thumbnails.medium.url} ${item.thumbnails.medium.width}w,`
            : ''
        const highSrcset: string = item.thumbnails.high
            ? `${item.thumbnails.high.url} ${item.thumbnails.high.width}w,`
            : ''
        const standardSrcset: string = item.thumbnails.standard
            ? `${item.thumbnails.standard.url} ${item.thumbnails.standard.width}w`
            : ''

        const defaultWebpSrcset: string =
            item.thumbnails.default && item.thumbnails.default.urlWebp
                ? `${item.thumbnails.default.urlWebp} ${item.thumbnails.default.width}w,`
                : ''
        const mediumWebpSrcset: string =
            item.thumbnails.medium && item.thumbnails.medium.urlWebp
                ? `${item.thumbnails.medium.urlWebp} ${item.thumbnails.medium.width}w,`
                : ''
        const highWebpSrcset: string =
            item.thumbnails.high && item.thumbnails.high.urlWebp
                ? `${item.thumbnails.high.urlWebp} ${item.thumbnails.high.width}w,`
                : ''
        const standardWebpSrcset: string =
            item.thumbnails.standard && item.thumbnails.standard.urlWebp
                ? `${item.thumbnails.standard.urlWebp} ${item.thumbnails.standard.width}w,`
                : ''
        const videoPlayerIconClass = StringUtils.isPlaylistJw(this.playlistId)
            ? ''
            : videoPlaylistClass.itemImgYT

        return `
            <li class="${videoPlaylistClass.item}"  ${
            ATTR_NAME.main.carousel.slideItem
        }>
                <button
                    type="button"
                    class="${videoPlaylistClass.itemBtn}"
                    ${ATTR_NAME.video.playlist.index}="${index}"
                    ${ATTR_NAME.video.playlist.videoPlaylistId}="${
            this.playlistId
        }"
                >
                    <div>
                        <picture class="${
                            videoPlaylistClass.itemImg
                        } ${videoPlayerIconClass}">
                            <source sizes="(min-width: 1200px) 10vw, (max-width: 480px) 50vw, 30vw"
                            srcset="${defaultWebpSrcset} ${mediumWebpSrcset} ${highWebpSrcset} ${standardWebpSrcset}" type="image/webp" />
                            <source sizes="(min-width: 1200px) 10vw, (max-width: 480px) 50vw, 30vw"
                            srcset="${defaultSrcset} ${mediumSrcset} ${highSrcset} ${standardSrcset}" type="image/jpg" />
                            <img src="${item.thumbnails.default.url}"  alt="" />
                            <div class="${
                                videoPlaylistClass.itemImgOverlay
                            }"></div>
                        </picture>
                        <div class="${videoPlaylistClass.itemContent}">
                            <span class="${videoPlaylistClass.itemTitle}">
                                <span class="${
                                    CLASS_NAME.common.visuallyHidden
                                }">play video: </span>
                                <span class="title-clamp">${item.title}</span>
                            </span>

                            <div class="${videoPlaylistClass.meta}">
                                <p class="${videoPlaylistClass.metaPubtime}">
                                    ${TimeUtils.renderUITimeAgo(
                                        item.publishedAt
                                    )}
                                </p>
                                <p class="video-playlist__queue-item-duration">
                                    ${TimeUtils.renderUIISODuration(
                                        item.duration
                                    )}
                                </p>
                            </div>
                        </div>
                    </div>
                </button>
            </li>`
    }

    /**
     * Handle the Event for clicking a queued video button
     *
     * @param {any} event Event object
     */
    private onClick(event: MouseEvent): void {
        // user has interacted!
        const btn = (event.target as HTMLElement).closest(
            `.${videoPlaylistClass.itemBtn}`
        )

        // type as number
        const index = btn
            ? btn.getAttribute(ATTR_NAME.video.playlist.index)
            : false
        const currentIndex = index ? parseFloat(index) : -1

        if (currentIndex === -1) {
            return
        }

        this.currentIndex = currentIndex

        PlaylistStore.getInstance().onClick(this.playlistId, currentIndex)
    }

    public removeNowPlaying(): void {
        if (!this.queueButtons) {
            return
        }

        this.queueButtons.forEach((button: HTMLElement) => {
            CSSUtils.removeClass(button, videoPlaylistClass.nowPlaying)
        })
    }

    public addNowPlaying(index: number): void {
        const element = this.getElement(
            `.${videoPlaylistClass.itemBtn}[${ATTR_NAME.video.playlist.index}="${index}"]`,
            this.el
        )

        CSSUtils.addClass(element, videoPlaylistClass.nowPlaying)
    }
}
